import Service, { type Registry as Services, service } from '@ember/service';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class BenchmarkInsightFeaturesService extends Service {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beniLightDropdownSegmentTooltipText() {
    if (this.access.hasBenchmarkInsightWriterRole) {
      return this.intl.t(
        'benchmark_insight.light.tooltip.segmentation_dropdown',
      );
    }

    return this.intl.t(
      'benchmark_insight.light.viewer.tooltip.segmentation_dropdown',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beniLightSegmentationTooltipText() {
    if (this.access.hasBenchmarkInsightWriterRole) {
      return this.intl.t('benchmark_insight.start_free_trial.segmentation');
    }

    return this.intl.t(
      'benchmark_insight.start_free_trial.segmentation.viewer',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get beniLightTooltipText() {
    if (this.access.hasBenchmarkInsightWriterRole) {
      return this.intl.t('benchmark_insight.start_free_trial');
    }

    return this.intl.t('benchmark_insight.light.viewer.start_free_trial');
  }

  get dropdownSegmentDisabledTooltip(): string | null {
    if (!this.showSegmentationNotAvailableOverlay) {
      return null;
    }

    if (this.showGoToMappingMessage) {
      return this.intl.t(
        'benchmark_insight.trial_without_mapping.header_segment_dropdown',
      );
    }

    return this.intl.t(
      'benchmark_insight.trial_without_data.header_segment_dropdown',
    );
  }

  get hasOwnPmsIntegration(): boolean {
    return this.hotelAndMarketService.hotel
      .benchmarkInsightHasOwnPmsIntegration;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBenchmarkInsightLight() {
    return this.hotelAndMarketService.hotel.benchmarkInsightIsLight;
  }

  get mappingCoverageOverThreshold(): boolean {
    return this.hotelAndMarketService.hotel
      .benchmarkInsightMappingCoverageOverThreshold;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showExportButtons() {
    if (this.isBenchmarkInsightLight) {
      return true;
    }

    return this.access.hasBenchmarkInsightExport;
  }

  get showGoToMappingMessage(): boolean {
    return this.hasOwnPmsIntegration && !this.mappingCoverageOverThreshold;
  }

  get showSectionSegment(): boolean {
    if (
      !this.access.hasBenchmarkInsightSegmentationPartial &&
      !this.hasOwnPmsIntegration &&
      !this.mappingCoverageOverThreshold
    ) {
      return false;
    }

    if (this.hasOwnPmsIntegration || this.mappingCoverageOverThreshold) {
      return true;
    }

    return false;
  }

  get showSegmentationNotAvailableOverlay(): boolean {
    return (
      !this.hasOwnPmsIntegration ||
      !this.mappingCoverageOverThreshold ||
      !this.access.hasBenchmarkInsightSegmentationPartial
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTrialCtas() {
    return this.showTrialTeasers && this.access.hasBenchmarkInsightTrialUpsell;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showTrialTeasers() {
    return (
      this.isBenchmarkInsightLight && !this.showSegmentationNotAvailableOverlay
    );
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/features': BenchmarkInsightFeaturesService;
  }
}
